import type { InfiniteLoader } from 'react-virtualized';
import type { RefObject } from 'react';

import React, { useImperativeHandle } from 'react';
import { Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';
import classNames from 'classnames';
import uniqBy from 'lodash/uniqBy';

import type { DropdownMenuProps } from '../Dropdown/DropdownMenu/DropdownMenu';
import type { DropdownContextProps, DropdownContextType } from '../Dropdown';
import type { ListProps } from '../List';

import { useDropdownContext, DropdownContext, DropdownMenu } from '../Dropdown';
import { assignRef } from '../../utils/assignRef';
import { useResponsive } from '../../hooks/useResponsive';
import { HelperMessage } from '../HelperMessage';
import { ReadOnlyField } from '../ReadOnlyField';
import { ErrorMessage } from '../ErrorMessage';
import { AddNewModal } from './AddNewModal';
import { TextField } from '../Fields';
import { Iconify } from '../Iconify';
import messages from './messages';
import { List } from '../List';
import { Chip } from '../Chip';
import Button from '../Button';
import './Autocomplete.scss';

const EMPTY_ARRAY: ListProps['items'] = [];

export interface RenderTriggerArgs {
  getReferenceProps: DropdownContextType['getReferenceProps'];
  setOpen: DropdownContextType['setOpen'];
  refs: DropdownContextType['refs'];
  helperText?: React.ReactNode;
  selected: ListProps['items'];
  disabled?: boolean;
  error: boolean;
  label: string;
}

export type AutocompleteOptionsRefType = RefObject<{
  selectOption: (option: ListProps['items'][number]) => void;
  getSelectedOptions: () => ListProps['items'];
}>;

export interface AutocompleteProps {
  classes?: {
    dropdownMenu?: DropdownMenuProps['classes'];
    triggerContainer?: string;
    readOnly?: string;
    input?: string;
    root?: string;
  };
  onIntersection?: ({
    startIndex,
    stopIndex,
  }: {
    startIndex: number;
    stopIndex: number;
  }) => Promise<unknown>;
  children?: ({
    onClear,
    onClose,
  }: {
    onClear: () => void;
    onClose: () => void;
  }) => React.ReactNode;
  onSearch?: (
    searchTerm: string,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => Promise<void>;
  renderTrigger?: (args: RenderTriggerArgs) => JSX.Element | null;
  optionsRef?: RefObject<AutocompleteOptionsRefType>['current'];
  onSelected?: (selected: ListProps['items']) => void;
  /**
   * If true, selected item will not be highlighted.
   */
  withoutSelectedHighlight?: boolean;
  defaultValue?: ListProps['items'];
  /**
   * CONTROLLED mode. `defaultValue` seeds the internal selection once (the sync
   * effect adopts it only while that selection is still empty), which leaves a
   * parent that NORMALIZES what it is handed — dropping an option the user just
   * checked, or substituting another — unable to make the checkmarks and the
   * count chip agree with what it actually applied. Pass the applied selection
   * here and every change to it is mirrored into the internal state.
   *
   * Omit it to keep the uncontrolled behaviour every other call site relies on.
   *
   * NOT named `value` on purpose: `FormAutocomplete` spreads react-hook-form's
   * whole `field` object in, so a prop by that name would silently put every
   * form autocomplete in the app into controlled mode — driven by whatever the
   * form state happens to hold (an id string, an array of ids), not by option
   * objects.
   */
  selection?: ListProps['items'];
  helperText?: React.ReactNode;
  addNewLabelHelpText?: string;
  options: ListProps['items'];
  onAddNewOpened?: () => void;
  onAddNewClosed?: () => void;
  errors?: string | string[];
  withoutDeselect?: boolean;
  withoutSearch?: boolean;
  'data-testid'?: string;
  emptyMessage?: string;
  searchLabel?: string;
  /**
   * Allows input of new non existing input. Both value and label and are same
   */
  withAddNew?: boolean;
  /**
   * Adds label for field which is inserting new option item.
   */
  addNewLabel?: string;
  readOnly?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  className?: string;
  /**
   * Caps the dropdown list height in px (how many option rows show before it
   * scrolls). Forwarded to the underlying List; omit to keep the default.
   */
  maxHeight?: number;
  loading?: boolean;
  label?: string;
  limit?: number;
  /**
   * Text shown in the empty input instead of `label`. `label` still floats
   * above the input once something is selected.
   */
  placeholder?: string;
  title: string;
  total: number;
}

const Autocomplete = React.forwardRef(
  (
    {
      addNewLabel,
      children,
      className,
      classes,
      'data-testid': testId,
      defaultValue,
      disabled,
      emptyMessage,
      errors,
      helperText,
      label,
      limit = 25,
      maxHeight,
      multiple,
      onAddNewOpened,
      onIntersection,
      onSearch,
      onSelected,
      options: propOptions,
      optionsRef: propsOptionsRef,
      placeholder,
      readOnly,
      renderTrigger,
      searchLabel,
      selection: controlledSelection,
      title,
      total,
      withAddNew,
      withoutDeselect,
      withoutSearch,
      withoutSelectedHighlight,
    }: AutocompleteProps,
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const optionsRef =
      React.useRef<AutocompleteOptionsRefType['current']>(null);

    const { formatMessage } = useIntl();
    const listRef = React.useRef<InfiniteLoader>(null);
    const { getReferenceProps, open, refs, setOpen } = useDropdownContext();
    // the breakpoint `DropdownMenu` turns into a sheet at
    const isMobile = useResponsive('down', 'sm');
    const [options, setOptions] =
      React.useState<ListProps['items']>(propOptions);
    const [selected, setSelected] = React.useState<ListProps['items']>(
      defaultValue ?? EMPTY_ARRAY,
    );
    const selectedRef = React.useRef<ListProps['items']>(
      defaultValue ?? EMPTY_ARRAY,
    );

    React.useEffect(() => {
      if (!open && onSearch) {
        onSearch('', { target: { value: '' } } as any);
      }
    }, [onSearch, open]);

    const readOnlyValue = React.useMemo(
      () => selected.map((item) => item.label).join(','),
      [selected],
    );

    /**
     * Controlled mode (see the `selection` prop): mirror the parent's applied
     * selection into internal state whenever the SET OF IDS changes. Comparing
     * the ids rather than the array identity is what keeps a parent that builds
     * the array inline from looping, and returning `prev` unchanged when they
     * match means an uncontrolled caller (no `selection`) is never touched.
     */
    const controlledKey = controlledSelection
      ?.map((item) => item.value)
      .join(' ');
    React.useEffect(() => {
      if (!controlledSelection) return;
      setSelected((prev) => {
        const prevKey = prev.map((item) => item.value).join(' ');
        return prevKey === controlledKey ? prev : controlledSelection;
      });
      // `controlledSelection` is intentionally not a dependency: its identity
      // changes on every parent render, `controlledKey` is its content.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controlledKey]);

    React.useEffect(() => {
      const missingOptions =
        defaultValue?.filter((value) =>
          propOptions.some((option) => option.value !== value.value),
        ) ?? EMPTY_ARRAY;
      const populatedOptions = uniqBy(
        [...propOptions, ...missingOptions],
        'value',
      );
      setOptions(populatedOptions);
      setSelected((prev) => {
        if (!prev || prev.length === 0)
          return (
            populatedOptions.filter((option) =>
              defaultValue?.some((value) => value.value === option.value),
            ) ?? prev
          );
        const resp = prev.map((item) => {
          if (
            populatedOptions.some((option) => option?.value === item?.value)
          ) {
            return populatedOptions.find(
              (option) => option.value === item.value,
            ) as ListProps['items'][number];
          }
          return item;
        });

        return resp;
      });
    }, [defaultValue, propOptions]);

    const handleSelect = React.useCallback(
      (option: ListProps['items'][number]) => {
        const isSelected = selected.some((selectedOption) => {
          return selectedOption.value === option.value;
        });

        if (multiple) {
          if (isSelected) {
            setSelected((prev) => {
              const newSelected = prev.filter((selectedOption) => {
                return selectedOption.value !== option.value;
              });
              onSelected?.(newSelected);
              return newSelected;
            });
          } else {
            setSelected((prev) => {
              const newSelected = uniqBy([...prev, option], 'value');
              onSelected?.(newSelected);
              return newSelected;
            });
          }
        } else {
          if (!withoutDeselect && selected[0]?.value === option.value) {
            setSelected([]);
            onSelected?.([]);
          } else {
            setSelected([option]);
            onSelected?.([option]);
          }
        }

        if (!multiple) {
          setOpen(false);
        }
      },
      [multiple, onSelected, selected, setOpen, withoutDeselect],
    );

    const onClear = React.useCallback(() => {
      setSelected([]);
      onSelected?.([]);
    }, [onSelected]);
    const stopPropagation = (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
    };
    const handleOpen = (e: React.MouseEvent<HTMLDivElement>) => {
      if (disabled) return;
      stopPropagation(e);
      setOpen((prev) => !prev);
    };

    const handleSearch = (
      e:
        | React.ChangeEvent<HTMLInputElement>
        | React.ChangeEvent<HTMLTextAreaElement>,
    ) => {
      if (onSearch) {
        onSearch(e.target.value, e as React.ChangeEvent<HTMLInputElement>).then(
          () => {
            listRef.current?.resetLoadMoreRowsCache(true);
          },
        );
      }
    };

    const handleChipRemove = React.useCallback(
      (
        e: React.MouseEvent<HTMLDivElement>,
        selected: ListProps['items'][number],
      ) => {
        e.stopPropagation();
        if (disabled) return;
        handleSelect(selected);
      },
      [handleSelect, disabled],
    );

    const inputLabel = React.useMemo(() => {
      return (
        <Typography className="Mfw-Autocomplete-inputLabel">
          {label || formatMessage(messages.select)}
        </Typography>
      );
    }, [label, formatMessage]);

    const emptyInputLabel = React.useMemo(() => {
      if (!placeholder) return inputLabel;
      return (
        <Typography className="Mfw-Autocomplete-inputLabel">
          {placeholder}
        </Typography>
      );
    }, [placeholder, inputLabel]);

    React.useEffect(() => {
      assignRef(propsOptionsRef || null, optionsRef.current);
    }, [propsOptionsRef]);

    React.useEffect(() => {
      selectedRef.current = selected;
    }, [selected]);

    useImperativeHandle(optionsRef, () => {
      return {
        getSelectedOptions: () => {
          return selectedRef.current;
        },
        selectOption: (option: ListProps['items'][number]) => {
          handleSelect(option);
        },
      };
    }, [handleSelect]);

    const renderSelectedComponent = React.useCallback(() => {
      if (renderTrigger) {
        return renderTrigger({
          disabled,
          error: !!errors,
          getReferenceProps,
          helperText,
          label: label || formatMessage(messages.select),
          refs,
          selected,
          setOpen,
        });
      }
      return (
        <div
          className={classNames('Mfw-Autocomplete-input', classes?.input, {
            'Mfw-Autocomplete-input--disabled': disabled,
          })}
        >
          {selected.length === 0 && emptyInputLabel}
          {!multiple && selected.length > 0 && (
            <Typography>{selected?.[0]?.label}</Typography>
          )}
          {multiple &&
            selected.map((option) => {
              return (
                <Chip
                  icon={
                    <div
                      onClick={(e) => handleChipRemove(e, option)}
                      className="mr-1"
                    >
                      <Iconify icon="material-symbols:close" size="small" />
                    </div>
                  }
                  className="bg-background-neutral"
                  label={option.label}
                  key={option.value}
                />
              );
            })}
        </div>
      );
    }, [
      renderTrigger,
      classes?.input,
      disabled,
      selected,
      emptyInputLabel,
      multiple,
      errors,
      getReferenceProps,
      helperText,
      label,
      formatMessage,
      refs,
      setOpen,
      handleChipRemove,
    ]);

    if (readOnly) {
      return (
        <ReadOnlyField
          className={classNames(
            'Mfw-Autocomplete',
            classes?.readOnly,
            className,
          )}
          label={label || formatMessage(messages.select)}
          value={readOnlyValue}
          errors={errors}
        />
      );
    }

    return (
      <div
        className={classNames('Mfw-Autocomplete', classes?.root, className)}
        ref={ref}
      >
        {!renderTrigger && selected.length > 0 && (
          <div className="Mfw-Autocomplete-inputLabel__wrapper">
            {inputLabel}
          </div>
        )}
        <div
          ref={(e) => {
            refs.setReference(e);
          }}
          className={'Mfw-Autocomplete-root'}
          data-testid={testId}
          onClick={handleOpen}
        >
          <div
            className={classNames(
              {
                'Mfw-Autocomplete-inputWrapper--withBorders': !renderTrigger,
                'Mfw-Autocomplete-inputWrapper--withErrorBorders':
                  Boolean(errors),
              },
              classes?.triggerContainer,
            )}
          >
            {renderSelectedComponent()}
          </div>
          {withAddNew && (
            <div className="w-full flex items-center justify-end mt-2">
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  onAddNewOpened?.();
                }}
                startIcon={{ type: 'add' }}
                variant="text"
              >
                {addNewLabel ?? formatMessage(messages.addNew)}
              </Button>
            </div>
          )}
          {helperText && (
            <HelperMessage className="mt-1 ml-4">{helperText}</HelperMessage>
          )}
          {errors && <ErrorMessage className="mt-1" errors={errors} />}
          <DropdownMenu classes={classes?.dropdownMenu} title={title}>
            <div
              className={classNames('Mfw-Autocomplete-menuContent', {
                // the phone's sheet: the list takes the room left under the
                // search field (`fill`), the actions stay below it
                'Mfw-Autocomplete-menuContent--fill': isMobile,
              })}
              {...getReferenceProps()}
              onClick={stopPropagation}
            >
              <div
                className={classNames('Mfw-Autocomplete-search', {
                  'Mfw-Autocomplete-search--hidden': withoutSearch,
                })}
              >
                <TextField
                  label={searchLabel ?? formatMessage(messages.search)}
                  onChange={handleSearch}
                  name={uuidv4()}
                  fullWidth
                />
              </div>
              {options.length === 0 && emptyMessage && (
                <div className="Mfw-Autocomplete-emptyMessageContainer">
                  <Typography>{emptyMessage}</Typography>
                </div>
              )}
              <List
                withoutSelectedHighlight={withoutSelectedHighlight}
                onIntersection={onIntersection || (async () => {})}
                className="Mfw-Autocomplete-list"
                onSelected={handleSelect}
                rowCount={options.length}
                // style={listStyles}
                maxHeight={maxHeight}
                selected={selected}
                fill={isMobile}
                items={options}
                total={total}
                limit={limit}
                ref={listRef}
              />
              {children?.({ onClear, onClose: () => setOpen(false) })}
            </div>
          </DropdownMenu>
        </div>
      </div>
    );
  },
);

Autocomplete.displayName = 'Autocomplete';
const AutocompleteDropdown = React.forwardRef(
  (
    {
      addNewLabel,
      addNewLabelHelpText,
      defaultOpen,
      onAddNewClosed,
      onAddNewOpened,
      options,
      optionsRef: propsOptionsRef,
      placement,
      withAddNew,
      ...other
    }: Omit<DropdownContextProps, 'children'> & AutocompleteProps,
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const optionsRef = React.useRef<
      AutocompleteOptionsRefType['current'] | null
    >(null);
    const [newOptions, setNewOptions] = React.useState<ListProps['items']>([]);
    const { formatMessage } = useIntl();
    const [isAddNewModalOpened, setIsAddNewModalOpened] = React.useState(false);

    const handleAddNewOpened = () => {
      onAddNewOpened?.();
      setIsAddNewModalOpened(true);
    };
    const handleAddNewClosed = () => {
      onAddNewClosed?.();
      setIsAddNewModalOpened(false);
    };

    const handleAddNew = (value: string) => {
      setNewOptions((prev) => [{ label: value, value }, ...prev]);
      onAddNewClosed?.();
      setIsAddNewModalOpened(false);
      optionsRef?.current?.selectOption({ label: value, value });
    };

    React.useEffect(() => {
      assignRef(propsOptionsRef || null, optionsRef.current);
    }, [propsOptionsRef]);

    const combinedOptions = React.useMemo(() => {
      return [...newOptions, ...options];
    }, [newOptions, options]);
    return (
      <>
        <DropdownContext defaultOpen={defaultOpen} placement={placement}>
          <Autocomplete
            onAddNewOpened={handleAddNewOpened}
            addNewLabel={addNewLabel}
            options={combinedOptions}
            optionsRef={optionsRef}
            withAddNew={withAddNew}
            ref={ref}
            {...other}
          />
        </DropdownContext>
        {withAddNew && (
          <AddNewModal
            label={addNewLabel || formatMessage(messages.addNew)}
            helpText={addNewLabelHelpText}
            onCancel={handleAddNewClosed}
            open={isAddNewModalOpened}
            onAddNew={handleAddNew}
          />
        )}
      </>
    );
  },
);

AutocompleteDropdown.displayName = 'AutocompleteDropdown';

export default AutocompleteDropdown;
